import { useMemo } from "react";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER, formatShortDate, formatShortWeekdayDate, formatTime } from "@shared";

import { AppointmentExchangeResponse } from "@/api";
import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { ExchangeView } from "@/pages/exchange/Exchange.types";
import { formatExchangeAppointment } from "@/pages/exchange/Exchange.utils";
import { useTranslation } from "@/translations";

import { ExchangeColumnWidth, formatRequestedPurposes } from "../ExchangeTable.utils";
import { ExchangeActionCell } from "../components/ExchangeActionCell";
import { ExchangeCountCell } from "../components/ExchangeCountCell";
import { ExchangeRequestedCell } from "../components/ExchangeRequestedCell";

type Cell = GridRenderCellParams<AppointmentExchangeResponse>;

interface Params {
  view: ExchangeView;
  isBusy: boolean;
  onMakeOffer: (row: AppointmentExchangeResponse) => void;
  onWithdrawOffer: (row: AppointmentExchangeResponse) => void;
  onViewOffers: (row: AppointmentExchangeResponse) => void;
  onDeleteRequest: (row: AppointmentExchangeResponse) => void;
}

export function useExchangeColumns({
  view,
  isBusy,
  onMakeOffer,
  onWithdrawOffer,
  onViewOffers,
  onDeleteRequest,
}: Params): Array<GridColDef<AppointmentExchangeResponse>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  return useMemo(() => {
    const locale = locales[language];

    const columns: Array<GridColDef<AppointmentExchangeResponse>> = [
      {
        field: "appointmentStartUtc",
        headerName: t("exchange.columns.date"),
        type: "date",
        flex: 1,
        maxWidth: ExchangeColumnWidth.date,
        renderCell: (params: Cell) => formatShortWeekdayDate(params.row.appointmentStartUtc, locale),
      },
      {
        field: "time",
        headerName: t("exchange.columns.time"),
        flex: 1,
        maxWidth: ExchangeColumnWidth.time,
        valueGetter: (_value, row) =>
          row.isAllDay ? -1 : row.appointmentStartUtc.getHours() * 60 + row.appointmentStartUtc.getMinutes(),
        renderCell: (params: Cell) =>
          params.row.isAllDay
            ? t("exchange.allDay")
            : `${formatTime(params.row.appointmentStartUtc, locale)} ${t("common.clock")}`,
      },
      {
        field: "location",
        headerName: t("exchange.columns.location"),
        flex: 1,
        maxWidth: ExchangeColumnWidth.location,
        valueGetter: (_value, row) => row.location ?? "",
        renderCell: (params: Cell) => params.row.location ?? EMPTY_VALUE_PLACEHOLDER,
      },
      {
        field: "createdAtUtc",
        headerName: t("exchange.columns.createdAt"),
        type: "date",
        flex: 1,
        maxWidth: ExchangeColumnWidth.createdAt,
        renderCell: (params: Cell) => formatShortDate(params.row.createdAtUtc, locale),
      },
      {
        field: "counselorUserCode",
        headerName: t("exchange.columns.from"),
        flex: 1,
        maxWidth: ExchangeColumnWidth.counselorUserCode,
      },
      {
        field: "purposeKey",
        headerName: t("exchange.columns.service"),
        flex: 1,
        maxWidth: ExchangeColumnWidth.purposeKey,
        valueGetter: (_value, row) => labels.purposeLabelOf(row.purposeKey),
        renderCell: (params: Cell) => <AppointmentPurposeChip label={labels.purposeLabelOf(params.row.purposeKey)} />,
      },
      {
        field: "requestedPurposeKeys",
        headerName: t("exchange.columns.requested"),
        flex: 2,
        minWidth: 220,
        valueGetter: (_value, row) => formatRequestedPurposes(row.requestedPurposeKeys, labels, t),
        renderCell: (params: Cell) => (
          <ExchangeRequestedCell
            text={formatRequestedPurposes(params.row.requestedPurposeKeys, labels, t)}
            note={params.row.note}
          />
        ),
      },
      {
        field: "offersCount",
        headerName: t("exchange.columns.offers"),
        flex: 1,
        maxWidth: ExchangeColumnWidth.offersCount,
        align: "center",
        headerAlign: "center",
        // On own rows the count doubles as a keyboard-reachable way into the overlay; the row click is mouse-only.
        renderCell: (params: Cell) => (
          <ExchangeCountCell
            value={params.row.offersCount}
            onClick={view === "mine" ? () => onViewOffers(params.row) : undefined}
            ariaLabel={t("exchange.actions.viewOffersAriaLabel", {
              count: params.row.offersCount,
              appointment: formatExchangeAppointment(params.row, locale, t),
            })}
          />
        ),
      },
      {
        field: "action",
        headerName: t("exchange.columns.action"),
        flex: 1,
        minWidth: ExchangeColumnWidth.action,
        sortable: false,
        filterable: false,
        renderCell: (params: Cell) => (
          <ExchangeActionCell
            row={params.row}
            view={view}
            isBusy={isBusy}
            onMakeOffer={onMakeOffer}
            onWithdrawOffer={onWithdrawOffer}
            onViewOffers={onViewOffers}
            onDeleteRequest={onDeleteRequest}
          />
        ),
      },
    ];

    return view === "others" ? columns : columns.filter((column) => column.field !== "counselorUserCode");
  }, [t, language, labels, view, isBusy, onMakeOffer, onWithdrawOffer, onViewOffers, onDeleteRequest]);
}
